<script
  setup
  generic="T extends RowData & { id?: string | number; subRows?: T[] }"
  lang="ts"
>
import type {
  Cell,
  ColumnDef,
  ColumnResizeMode,
  ExpandedState,
  GroupColumnDef,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/vue-table'
import {
  FlexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { useDebounceFn, useResizeObserver } from '@vueuse/core'
import type { ComponentPublicInstance, UnwrapRef } from 'vue'
import { computed, useSlots, useTemplateRef } from '#imports'
import VirtualDataGridHeader from './VirtualDataGridHeader.vue'

const sortingState = defineModel<SortingState>('sorting', {
  default: () => [],
})
const selectedState = defineModel<RowSelectionState>('selecting', {
  default: () => ({}),
})
const columnVisibility = defineModel<VisibilityState>('columnVisibility', {
  default: () => ({}),
})
const expandedState = defineModel<ExpandedState>('expanded', {
  default: () => ({}),
})

const {
  activeId,
  activeKey,
  columns,
  count,
  data,
  headerType = 'single',
  height,
  manualSorting,
  manualFiltering = true,
  columnResizeMode = 'onChange',
  overscan,
  removePadding,
  loading,
  rowHeight = 33,
  disableMeasure,
  clickableSubRows,
} = defineProps<{
  activeId?: string
  activeKey?: keyof T
  // oxlint-disable-next-line typescript/no-explicit-any -- TValue is per-column; matches TanStack's own TableOptions.columns
  columns: ColumnDef<T, any>[]
  count?: number
  data: T[]
  headerType?: 'single' | 'grouped'
  /** height of table, in pixels. Needed for proper scrollbar height */
  height: number
  loading?: boolean
  manualFiltering?: boolean
  manualSorting?: boolean
  overscan?: number
  removePadding?: boolean
  /** estimate row height for accurate scrollbar dragging */
  rowHeight?: number
  columnResizeMode?: ColumnResizeMode
  hasFirstDataLoaded?: boolean
  disableMeasure?: boolean
  /** emit `click:row` for the sub-row itself instead of letting the click reach its parent row */
  clickableSubRows?: boolean
  width: 'max-content' | '100%' | number
}>()

if (import.meta.dev) {
  /** Dev only validation */
  if (
    headerType === 'single' &&
    columns.some((col) => (col as GroupColumnDef<T>).columns?.length)
  ) {
    // oxlint-disable-next-line no-console
    console.error(
      'DEV ERROR: When using single headers, column definitions must not have child columns',
      'headerType: "single"',
      columns.filter((col) => (col as GroupColumnDef<T>).columns?.length),
    )
  }
}

const emit = defineEmits<{
  'click:row': [row: Row<T>]
  'load-more': []
  'expand:row': [row: Row<T>]
}>()

const table = useVueTable({
  autoResetAll: true,
  enableRowSelection: true,
  enableExpanding: true,
  get data() {
    return data
  },
  get columns() {
    return columns
  },
  getSubRows: (row) => row.subRows,
  getCoreRowModel: getCoreRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getRowId: (row, index) => row.id?.toString() || index.toString(),
  debugTable: true,
  manualSorting,
  manualFiltering,
  columnResizeMode,
  state: {
    get columnVisibility() {
      return columnVisibility.value
    },
    get sorting() {
      return sortingState.value
    },
    get rowSelection() {
      return selectedState.value
    },
    get expanded() {
      return expandedState.value
    },
  },
  onSortingChange: (updaterOrValue) => {
    sortingState.value = (
      typeof updaterOrValue === 'function' && sortingState.value
        ? updaterOrValue(sortingState.value)
        : updaterOrValue
    ) as SortingState
  },
  onRowSelectionChange: (updateOrValue) => {
    selectedState.value =
      typeof updateOrValue === 'function'
        ? updateOrValue(selectedState.value)
        : updateOrValue
  },
  onExpandedChange: (updateOrValue) => {
    expandedState.value =
      typeof updateOrValue === 'function'
        ? updateOrValue(expandedState.value)
        : updateOrValue
  },
  onColumnVisibilityChange: (updateOrValue) => {
    columnVisibility.value =
      typeof updateOrValue === 'function'
        ? updateOrValue(columnVisibility.value)
        : updateOrValue
  },
})

const rows = computed(() =>
  table.getRowModel().rows.filter((row) => row.depth === 0),
)

//The virtualizer needs to know the scrollable container element
const tableContainerRef = useTemplateRef('tableContainerRef')

const rowVirtualizerOptions = computed<
  UnwrapRef<Parameters<typeof useVirtualizer>['0']>
>(() => {
  return {
    count: Math.max(rows.value.length, count ?? 0),
    estimateSize: () => rowHeight,
    getScrollElement: () => tableContainerRef.value,
    overscan: overscan ?? 10,
  }
})

const rowVirtualizer = useVirtualizer(rowVirtualizerOptions)

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems())
const totalSize = computed(() => rowVirtualizer.value.getTotalSize())

function measureElement(el: Element | ComponentPublicInstance | null): void {
  if (disableMeasure || !el) {
    return
  }
  rowVirtualizer.value.measureElement(el as Element)

  return undefined
}

function getTdClasses(cell: Cell<T, unknown>) {
  const meta = cell.column.columnDef.meta || {}
  const classes = []

  if (meta.className) {
    classes.push(...meta.className.split(' '))
  }

  if (cell.column.getCanSort()) {
    if (meta.align === 'right') {
      classes.push('pl-6')
    } else {
      classes.push('pr-6')
    }
  }

  return classes.join(' ')
}

function getTdStyles(cell: Cell<T, unknown>) {
  const meta = cell.column.columnDef.meta || {}
  return {
    display: 'flex',
    width: `${cell.column.getSize()}px`,
    justifyContent:
      meta.align === 'right'
        ? 'end'
        : meta.align === 'center'
          ? 'center'
          : 'start',
    alignItems: meta.verticalAlign || 'center',
  }
}

const headerGroups = computed(() => table.getHeaderGroups()[0]!.headers)

const getRowIsActive = (row: Row<T>): boolean => {
  if (!row || !activeId) {
    return false
  }

  const item = row.original as Record<keyof T | 'id', any>
  const valueToCheck =
    activeKey && Object.hasOwn(item, activeKey)
      ? `${item[activeKey]}`
      : (item.id?.toString() ?? null)

  return valueToCheck === activeId
}

const getRowClasses = (row: Row<T>): string[] => {
  return [
    'border-b border-neutral-200',
    getRowIsActive(row) ? 'bg-primary-50' : 'hover:bg-surface-bg',
    removePadding ? 'px-0' : 'px-4 lg:px-6',
  ]
}

function onSubRowClick(event: MouseEvent, subRow: Row<T>) {
  if (!clickableSubRows) return
  event.stopPropagation()
  emit('click:row', subRow)
}

function onSubRowKeydown(event: KeyboardEvent, subRow: Row<T>) {
  if (!clickableSubRows || (event.key !== 'Enter' && event.key !== ' ')) return
  // keys bubbling from a control inside the row keep that control's behavior
  if (event.target !== event.currentTarget) return
  event.preventDefault()
  event.stopPropagation()
  emit('click:row', subRow)
}

function getVisibleSubRows(row: Row<T>): Row<T>[] {
  const result: Row<T>[] = []
  for (const subRow of row.subRows) {
    result.push(subRow)
    if (subRow.subRows?.length && subRow.getIsExpanded()) {
      result.push(...getVisibleSubRows(subRow))
    }
  }
  return result
}

const onVirtualScroll = useDebounceFn((event) => {
  const el = event.target
  const buffer = 2

  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= buffer

  if (atBottom && !loading) {
    emit('load-more')
  }
}, 600)

const slots = useSlots()
const hasSlotContent = computed(
  () => !!slots.append && slots.append().length > 0,
)

const tanTable = useTemplateRef<HTMLElement>('tan-table')
const appendContainer = useTemplateRef('append-container')

useResizeObserver(tanTable, ([entry]) => {
  const width = entry!.contentRect.width
  if (appendContainer.value) {
    appendContainer.value.style.width = `${width}px`
  }
})

defineExpose({
  table,
})
</script>

<template>
  <div
    ref="tableContainerRef"
    class="z-0"
    :style="{
      overflow: 'auto', // our scrollable table container
      position: 'relative', // needed for sticky header
      height: `${height}px`,
    }"
    @scroll="onVirtualScroll"
  >
    <div :style="{ height: `${totalSize}px` }">
      <!-- Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights -->
      <table ref="tan-table" :style="{ display: 'grid', width }">
        <thead
          :style="{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }"
        >
          <tr
            class="bg-surface-bg easing-in z-1 items-center border-b transition"
            :class="{
              'px-4 lg:px-6': !removePadding,
            }"
            :style="{ display: 'flex', width: '100%' }"
          >
            <th
              v-for="header in headerGroups"
              :key="header.id"
              class="p-0"
              :class="header.column.columnDef.meta?.className || ''"
              :style="{
                width: `${header.getSize()}px`,
              }"
            >
              <div>
                <!-- top row: group label when present, otherwise an empty bar to match sibling group headers -->
                <div
                  v-if="headerType === 'grouped'"
                  class="bg-neutral-100 p-2.5 text-left text-xs font-medium text-neutral-800 first:pl-6"
                >
                  {{
                    header.column.columns.length
                      ? header.column.columnDef.header
                      : '\u00A0'
                  }}
                </div>
                <div class="flex">
                  <template v-if="header.subHeaders.length">
                    <div
                      v-for="subHeader in header.subHeaders"
                      :key="subHeader.id"
                      :style="{
                        width: `${subHeader.getSize()}px`,
                      }"
                    >
                      <VirtualDataGridHeader
                        :columnSizingInfo="table.getState().columnSizingInfo"
                        :header="subHeader"
                      >
                        <div v-if="!subHeader.isPlaceholder">
                          <FlexRender
                            :props="subHeader.getContext()"
                            :render="subHeader.column.columnDef.header"
                          />
                        </div>
                      </VirtualDataGridHeader>
                    </div>
                  </template>
                  <template v-else>
                    <VirtualDataGridHeader
                      :columnSizingInfo="table.getState().columnSizingInfo"
                      :header="header"
                    >
                      <div v-if="!header.isPlaceholder">
                        <FlexRender
                          :props="header.getContext()"
                          :render="header.column.columnDef.header"
                        />
                      </div>
                    </VirtualDataGridHeader>
                  </template>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody v-if="!hasFirstDataLoaded && loading" class="bg-surface">
          <tr
            v-for="_row in virtualRows.length || 10"
            class="flex w-full animate-pulse border-b border-neutral-200 px-4"
          >
            <td
              v-for="header in headerGroups"
              :key="`${header.id}-skeleton`"
              class="p-3"
              :style="{
                width: `${header.getSize()}px`,
              }"
            >
              <div class="flex">
                <div class="h-5.5 w-full rounded-lg bg-gray-200" />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody
          v-else
          class="bg-surface"
          :style="{
            display: 'grid',
            height: `${totalSize}px`, //tells scrollbar how big the table is
            position: 'relative', //needed for absolute positioning of rows
          }"
        >
          <tr
            v-for="vRow in virtualRows"
            :ref="measureElement"
            :key="`${rows[vRow.index]!.id}-${vRow.index}`"
            class="group border-b text-sm"
            :class="[
              getRowClasses(rows[vRow.index]!),
              { 'flex-col': rows[vRow.index]!.getIsExpanded() },
            ]"
            :style="{
              display: 'flex',
              position: 'absolute',
              transform: `translateY(${vRow.start}px)`,
              width: '100%',
            }"
            :data-index="
              vRow.index /* needed for dynamic row height measurement*/
            "
            @click="emit('click:row', rows[vRow.index]!)"
          >
            <div class="flex w-full">
              <td
                v-for="cell in rows[vRow.index]!.getVisibleCells()"
                :key="cell.id"
                class="px-4 py-3 text-sm lg:px-6"
                :class="getTdClasses(cell)"
                :style="getTdStyles(cell)"
              >
                <FlexRender
                  :props="cell.getContext()"
                  :render="cell.column.columnDef.cell"
                />
              </td>
            </div>
            <div
              v-if="
                rows[vRow.index]!.subRows?.length &&
                rows[vRow.index]!.getIsExpanded()
              "
              class="w-full border-t border-neutral-50 bg-neutral-50 text-neutral-900"
            >
              <div
                v-for="subRow in getVisibleSubRows(rows[vRow.index]!)"
                :key="subRow.id"
                :role="clickableSubRows ? 'link' : undefined"
                class="flex w-full border-t border-neutral-100"
                :class="{
                  'group/sub-row cursor-pointer hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none':
                    clickableSubRows,
                }"
                :tabindex="clickableSubRows ? 0 : undefined"
                @click="onSubRowClick($event, subRow)"
                @keydown="onSubRowKeydown($event, subRow)"
              >
                <td
                  v-for="cell in subRow.getVisibleCells()"
                  :key="cell.id"
                  class="px-4 py-3 text-sm lg:px-6"
                  :class="getTdClasses(cell)"
                  :style="getTdStyles(cell)"
                >
                  <FlexRender
                    :props="cell.getContext()"
                    :render="cell.column.columnDef.cell"
                  />
                </td>
              </div>
            </div>
          </tr>
        </tbody>
      </table>
      <div v-if="hasSlotContent" ref="append-container" class="relative">
        <div class="sticky left-1/2 max-w-max -translate-x-1/2">
          <slot name="append" />
        </div>
      </div>
    </div>
  </div>
</template>
